import { computed, onScopeDispose, ref } from 'vue';
import { defineStore } from 'pinia';
import { getMobileClient, type MobileUpdateState } from '@/services/mobile-client';

export const useMobileUpdateStore = defineStore('mobile-updates', () => {
  const adapter = getMobileClient()?.updates;
  const available = !!adapter;
  const state = ref<MobileUpdateState | null>(null);
  const error = ref('');
  const dismissedVersion = ref('');
  const busy = computed(() => ['checking', 'downloading'].includes(state.value?.phase ?? ''));
  let starting: Promise<void> | null = null;
  let unsubscribe: (() => void) | undefined;
  let disposed = false;
  let lastAttempt = 0;

  function accept(next: MobileUpdateState): void {
    if (!disposed && (!state.value || next.revision >= state.value.revision)) state.value = next;
  }
  async function run(action: 'status' | 'check' | 'download' | 'install'): Promise<void> {
    if (!adapter || (action !== 'status' && state.value?.phase === 'disabled')) return;
    error.value = '';
    try {
      accept(await adapter[action]());
    } catch (cause) {
      error.value = (cause as Error).message || '更新操作失败，请重试';
    }
  }
  async function check(manual = true): Promise<void> {
    if (!adapter || busy.value || state.value?.phase === 'disabled') return;
    const now = Date.now();
    if (
      !manual &&
      (now - lastAttempt < 5 * 60_000 || now - (state.value?.lastCheckedAt ?? 0) < 6 * 60 * 60_000)
    )
      return;
    lastAttempt = now;
    await run('check');
  }
  async function setAutoDownload(enabled: boolean): Promise<void> {
    if (!adapter || state.value?.phase === 'disabled') return;
    error.value = '';
    try {
      accept(await adapter.setAutoDownload({ enabled }));
      if (enabled && state.value?.phase === 'available') await run('download');
    } catch (cause) {
      error.value = (cause as Error).message || '更新偏好保存失败，请重试';
    }
  }
  async function refresh(): Promise<void> {
    await run('status');
    if (disposed) return;
    const value = state.value;
    if (
      value?.autoDownload &&
      value.latestVersion &&
      ['available', 'error'].includes(value.phase) &&
      Date.now() - lastAttempt >= 5 * 60_000
    ) {
      lastAttempt = Date.now();
      await run('download');
    } else await check(false);
  }
  const resume = () => {
    if (!document.hidden && navigator.onLine) void refresh();
  };
  function start(): Promise<void> {
    if (!adapter) return Promise.resolve();
    return (starting ??= (async () => {
      try {
        const stop = await adapter.onChange(accept);
        if (disposed) {
          stop();
          return;
        }
        unsubscribe = stop;
        window.addEventListener('mobile:resume', resume);
        window.addEventListener('online', resume);
        await refresh();
      } catch (cause) {
        error.value = (cause as Error).message || '无法初始化更新服务，请重新打开 APP';
      }
    })());
  }
  onScopeDispose(() => {
    disposed = true;
    unsubscribe?.();
    window.removeEventListener('mobile:resume', resume);
    window.removeEventListener('online', resume);
  });
  return { available, state, error, busy, dismissedVersion, start, check, run, setAutoDownload };
});
